import React, { RefObject, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Fit from 'react-fit';

import { Input } from '../../atoms';
import { useOnClickOutside } from '../../../hooks';

import {
  InputContainer,
  Container,
  OptionsContainer,
  RelativeColumn,
} from './TimePicker.styles';

import { getTimeOptions, getInputValue } from './utils';
import { Time, TimePickerTranslations } from './types';
import { defaultTranslations } from './translations';
import Options from '../../atoms/Options';
import { DropdownAlignment } from '../../atoms/types';

const minutesOptions = getTimeOptions(60);
const hoursOptions = getTimeOptions(24);

export interface TimePickerProps {
  /**
   * Time Picker value
   */
  value?: Time;
  /**
   * Component change event handler
   */
  onChange: (time: Time) => void;
  /**
   * Portal container used to mount options component
   */
  portalContainer?: RefObject<HTMLElement>;
  /**
   * Component translations
   */
  translations?: TimePickerTranslations;
  /**
   * Modal container - can be used handle outside click
   */
  modalRef?: RefObject<HTMLElement>;
  /**
   * Timepicker disabled state
   */
  disabled?: boolean;
  /**
   * Dropdown alignment
   */
  dropdownAlignment?: DropdownAlignment;
}

/**
- The TimePicker allows users to enter time by choosing values from dropdown lists.
**/
const TimePicker = ({
  value,
  portalContainer,
  translations = defaultTranslations,
  onChange,
  modalRef,
  disabled,
  dropdownAlignment = 'left',
}: TimePickerProps) => {
  const [showPicker, setPicker] = useState(false);
  const pickerContainerRef = useRef(null);
  const { minute, hour } = value || {};

  const { placeholder } = translations;
  const portalElement = portalContainer?.current;

  useOnClickOutside(
    [pickerContainerRef, portalContainer],
    () => {
      if (showPicker) {
        setPicker(false);
      }
    },
    modalRef?.current
  );

  const calculateDropdownPosition = useCallback(() => {
    const { current: element } = pickerContainerRef;

    const { top, height, left, width } = element.getBoundingClientRect();

    return {
      top: top + height + window.scrollY,
      left: left + window.scrollX,
      width,
    };
  }, [pickerContainerRef]);

  const renderOptions = () => (
    <OptionsContainer>
      <RelativeColumn>
        <Options
          alignment={dropdownAlignment}
          options={hoursOptions}
          onSelect={(_e, { value }) =>
            onChange({ minute: minute || 0, hour: Number(value) })
          }
          selectSize="small"
          selectionIndex={hoursOptions.findIndex(({ value }) => value === hour)}
        />
      </RelativeColumn>
      <RelativeColumn>
        <Options
          alignment={dropdownAlignment}
          options={minutesOptions}
          onSelect={(_e, { value }) =>
            onChange({ hour: hour || 0, minute: Number(value) })
          }
          selectSize="small"
          selectionIndex={minutesOptions.findIndex(
            ({ value }) => value === minute
          )}
        />
      </RelativeColumn>
    </OptionsContainer>
  );

  return (
    <Container ref={pickerContainerRef}>
      <div
        onClick={() => {
          !disabled && setPicker(!showPicker);
        }}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        data-testid="time-picker-input"
      >
        <InputContainer>
          <Input
            onChange={() => false}
            inputSize="small"
            iconRight="clock"
            placeholder={placeholder}
            readOnly
            value={getInputValue(value)}
            disabled={disabled}
          />
        </InputContainer>
      </div>
      {showPicker && (
        <>
          {portalElement ? (
            <>
              {createPortal(
                <div
                  style={{
                    position: 'absolute',
                    ...calculateDropdownPosition(),
                  }}
                >
                  {renderOptions()}
                </div>,
                portalElement
              )}
            </>
          ) : (
            <Fit>
              <div
                style={{ position: 'absolute', width: '100%' }}
                data-testid="time-picker-container"
              >
                {renderOptions()}
              </div>
            </Fit>
          )}
        </>
      )}
    </Container>
  );
};

export default TimePicker;
